import { useEffect } from "react";
import type { WsClient } from "@/lib/ws-client";

/**
 * Live-batch-surfaces Task 2 — register `refetch` against a set of WS event
 * types for the lifetime of the component. This is the shared form of the
 * effect useQueues (R-182) and useQueueCounts hand-rolled; batch list/detail
 * are consumers 3 and 4, so the loop moves here instead of multiplying.
 *
 * `channel` additionally subscribes an explicit channel (e.g. `batch:<id>`)
 * while mounted. WsClient refcounts desired channels and replays them on
 * every reconnect's auth_ok, so a mount/unmount pair here is safe under
 * StrictMode double-mounting and network blips alike.
 *
 * `events` MUST be a module-level const — the array is an effect dep by
 * identity, so a fresh literal per render would rewire the handlers on
 * every render.
 *
 * A null `wsClient` no-ops (provider not mounted / not yet connected) — the
 * caller's poll safety net still provides eventual freshness.
 */
export function useWsRefetch(
  wsClient: WsClient | null,
  events: readonly string[],
  refetch: () => void | Promise<void>,
  channel?: string,
): void {
  useEffect(() => {
    if (!wsClient) return;

    const offs = events.map((event) =>
      wsClient.on(event, () => {
        void refetch();
      }),
    );
    if (channel) wsClient.subscribe(channel);

    return () => {
      offs.forEach((off) => off?.());
      if (channel) wsClient.unsubscribe(channel);
    };
  }, [wsClient, refetch, channel, events]);
}
